import { WidgetContainer } from '@/components/analytics/WidgetContainer';
import type { SessionActivity } from '@/types/analytics';
import styles from './TotalQuestionsAnswered.module.scss';

export interface TotalQuestionsAnsweredProps {
  data: SessionActivity | null;
  previousPeriodData?: SessionActivity | null;
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Total Questions Answered Widget
 *
 * Displays total questions completed with trend comparison.
 * Shows increase/decrease from previous period.
 */
export function TotalQuestionsAnswered({
  data,
  previousPeriodData,
  isLoading,
  error,
}: TotalQuestionsAnsweredProps) {
  const isEmpty = !data || data.totalQuestions === 0;

  // Calculate trend
  const calculateTrend = (): { percentage: number; isPositive: boolean } | null => {
    if (!data || !previousPeriodData || previousPeriodData.totalQuestions === 0) {
      return null;
    }

    const difference = data.totalQuestions - previousPeriodData.totalQuestions;
    const percentage = (difference / previousPeriodData.totalQuestions) * 100;
    return {
      percentage: Math.abs(percentage),
      isPositive: difference > 0,
    };
  };

  const trend = calculateTrend();

  return (
    <WidgetContainer
      title="Total Questions Answered"
      description="Cumulative questions completed"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage="Start practicing to track your progress"
      size="small"
    >
      <div className={styles.totalQuestions}>
        {/* Main Value */}
        <div className={styles.totalQuestions__main}>
          <div className={styles.totalQuestions__value}>
            {data?.totalQuestions.toLocaleString() || 0}
          </div>
          <div className={styles.totalQuestions__label}>Questions</div>
        </div>

        {/* Trend Indicator */}
        {trend && (
          <div
            className={`${styles.totalQuestions__trend} ${
              trend.isPositive
                ? styles['totalQuestions__trend--positive']
                : styles['totalQuestions__trend--negative']
            }`}
          >
            <span className={styles.totalQuestions__trendIcon}>
              {trend.isPositive ? '↑' : '↓'}
            </span>
            <span className={styles.totalQuestions__trendValue}>
              {trend.percentage.toFixed(0)}%
            </span>
            <span className={styles.totalQuestions__trendLabel}>vs. previous period</span>
          </div>
        )}

        {/* Additional Stats */}
        {data && (
          <div className={styles.totalQuestions__stats}>
            <div className={styles.totalQuestions__stat}>
              <span className={styles.totalQuestions__statValue}>
                {data.totalSessions.toLocaleString()}
              </span>
              <span className={styles.totalQuestions__statLabel}>Sessions</span>
            </div>
            <div className={styles.totalQuestions__stat}>
              <span className={styles.totalQuestions__statValue}>
                {data.totalSessions > 0
                  ? Math.round(data.totalQuestions / data.totalSessions)
                  : 0}
              </span>
              <span className={styles.totalQuestions__statLabel}>Avg per Session</span>
            </div>
          </div>
        )}
      </div>
    </WidgetContainer>
  );
}
